import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';
import type { OnboardingDraft } from '../shared/useOnboardingDraft';
import { stockValidationCopy as copy } from './copy';

const SYMBOL_PATTERN = /^[A-Z0-9][A-Z0-9.-]{0,11}$/;
const EXCHANGE_MAX_CHARS = 60;

function normalizeSymbol(value: string): string {
  return value.trim().toUpperCase();
}

function isSymbolValid(value: string): boolean {
  return SYMBOL_PATTERN.test(normalizeSymbol(value));
}

export function isStockStepValid(draft: OnboardingDraft): boolean {
  const exchange = draft.exchange.trim();
  return exchange.length > 0 && exchange.length <= EXCHANGE_MAX_CHARS && isSymbolValid(draft.symbol);
}

/** The project name: "AAPL (NASDAQ)". */
export function stockProjectName(draft: OnboardingDraft): string {
  return `${normalizeSymbol(draft.symbol)} (${draft.exchange.trim()})`;
}

/**
 * The project prompt every stage is generated from: the stock to research,
 * named so the stages' web search finds the right listing. The first line
 * of prose becomes the project card's summary.
 */
export function composeStockPrompt(draft: OnboardingDraft): string {
  const symbol = normalizeSymbol(draft.symbol);
  const exchange = draft.exchange.trim();
  return [
    `# ${symbol} (${exchange})`,
    '',
    `Research the publicly listed company whose shares trade under the symbol ${symbol} on ${exchange}, and decide whether to buy, hold or sell the stock.`,
    '',
    `- Exchange: ${exchange}`,
    `- Stock symbol (ticker): ${symbol}`,
  ].join('\n');
}

interface StockSymbolStepProps {
  draft: OnboardingDraft;
  onChange: (patch: Partial<OnboardingDraft>) => void;
}

/** The one screen of the stock-validation wizard: the exchange and the ticker symbol. */
export function StockSymbolStep({ draft, onChange }: StockSymbolStepProps) {
  const symbolError = draft.symbol.trim() !== '' && !isSymbolValid(draft.symbol);

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" component="h2" fontWeight={700}>
          {copy.stock.heading}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {copy.stock.helper}
        </Typography>
      </Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
        <Autocomplete
          freeSolo
          options={copy.exchanges}
          inputValue={draft.exchange}
          onInputChange={(_, exchange) => onChange({ exchange: exchange.slice(0, EXCHANGE_MAX_CHARS) })}
          sx={{ flex: 2 }}
          renderInput={(params) => (
            <TextField {...params} label={copy.stock.exchangeLabel} placeholder={copy.stock.exchangePlaceholder} required autoFocus />
          )}
        />
        <TextField
          label={copy.stock.symbolLabel}
          placeholder={copy.stock.symbolPlaceholder}
          required
          value={draft.symbol}
          onChange={(e) => onChange({ symbol: e.target.value.toUpperCase() })}
          error={symbolError}
          helperText={symbolError ? copy.stock.symbolInvalid : ' '}
          slotProps={{ htmlInput: { maxLength: 12, autoCapitalize: 'characters', spellCheck: false } }}
          sx={{ flex: 1 }}
        />
      </Stack>
    </Stack>
  );
}
